import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUserToRequest } from "../manage-coaches/utils/manage-coaches-utils";
import { isCoach, isCoachFreeTrial } from "../../../../utils/user-helpers";
import { get } from "lodash";
import ToolTip from "../../../../parts/tooltip-portal";
import SvgIcon from "../../../../parts/SvgIcon";
import ThemeSelector from "./ThemeSelector";
import MultiOptionsInput from "./MultiOptionsInput";
import InputColor from "./InputColor";
import ImageSelector from "./ImageSelector";
import { GradientRange } from "../../../../parts/inputs/GradientRange";
import ImageSelectorSmall from "./ImageSelectorSmall";

export const MobileBrandingSection = ({
  active = false,
  state,
  locals,
  handleChange,
  handleSwitch,
  renderMobilePreview,
  renderMobileSplashPreview,
  onGradientRangeChange,
}) => {
  const {
    businessName,
    googlePlayLink,
    appStoreLink,
    appMainColor,
    isAppDefaultColor,
    isSplashImgTinted,
    logoImg = {},
    formLogoImg = {},
    mobileSplashImg = {},
    formMobileSplashImg,
    mobileSplashChanged,
    mobileSplashBg = {},
    formMobileSplashBg,
    mobileSplashBgChanged,
    splashOverlay,
    splashOverlayColor,
    splashOverlayOpacity,
    logoChanged,
    appTheme,
    appSecondaryTextColor,
    temporalSplashImg,
    temporalSplashBg,
  } = state;

  // * had to handle this with JS because if not we have duplicated inputs in the DOM
  // * and the inputs are not being updated correctly because they have the same name
  const [viewportWidth, setViewportWidth] = useState(null);

  useEffect(() => {
    !viewportWidth && setViewportWidth(window?.innerWidth || null);
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  const updateDimensions = () => {
    setViewportWidth(window?.innerWidth || null);
  };

  const { activeUser, organizationOwner } = locals;
  const userToRequest = getUserToRequest(activeUser, organizationOwner);

  const athletesCount = parseInt(
    userToRequest && userToRequest.athletes && userToRequest.athletes.length
      ? userToRequest.athletes.length
      : 0
  );

  const userIsTrial = isCoachFreeTrial(userToRequest);
  const userIsCoach = isCoach(userToRequest);
  const enableCustomIcon = athletesCount >= 10 && !userIsTrial && userIsCoach;

  const userHasCustomIcon = get(userToRequest, "customMobileIcon", false);

  const userIsEnableToUpdateAppStoreListing = get(
    userToRequest,
    "appStoreListingEnable",
    false
  );
  const referAsClients = activeUser?.referAsClients;
  const { t } = useTranslation(["dashboard", "common"]);
  const countNeeded = 10 - athletesCount;
  const customIconLabel = userHasCustomIcon
    ? t("branding.youHaveCustomAppIcon")
    : athletesCount < 10
    ? referAsClients
      ? countNeeded === 1
        ? t("branding.moreClientRequired", { count: 1 })
        : t("branding.moreClientsRequired", { count: countNeeded })
      : countNeeded === 1
      ? t("branding.moreAthleteRequired", { count: 1 })
      : t("branding.moreAthletesRequired", { count: countNeeded })
    : t("branding.eligibleCustomAppIcon");

  return (
    <div
      style={{
        display: active ? "block" : "none",
      }}
      className="mobile-section"
    >
      <article>
        <p className="s1">{t("branding.customAppIcon")}</p>
        <div className="flex gap-2">
          <div className={`chip ${userHasCustomIcon ? "info" : "warning"}`}>
            {customIconLabel}
          </div>
          <a
            role="button"
            data-button-type="outline"
            className={`custom-button${
              enableCustomIcon || userHasCustomIcon ? " active" : ""
            } h-8`}
            style={{
              cursor:
                enableCustomIcon || userHasCustomIcon ? "pointer" : "default",
              pointerEvents:
                enableCustomIcon || userHasCustomIcon ? "auto" : "none",
            }}
            href={
              userHasCustomIcon
                ? "https://form.typeform.com/to/Fm3TuJOF"
                : "https://form.typeform.com/to/teJvkEZE"
            }
            target="_blank"
            rel="noopener noreferrer"
            disabled={enableCustomIcon || userHasCustomIcon ? undefined : true}
          >
            <span>{userHasCustomIcon ? t("branding.changeIcon") : t("branding.getNow")}</span>
          </a>
        </div>
      </article>
      <article>
        <header>
          <p className="s1">{t("branding.customAppStoreListing")}</p>
          <ToolTip body={t("branding.appStoreListingTooltip")}>
            <SvgIcon
              alt={t("branding.infoIconAlt")}
              classNames="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        </header>
        <div className="flex gap-6">
          <label className="grow">
            <span>{t("branding.appStoreLink")}</span>
            <input
              type="text"
              name="appStoreLink"
              className="text-input"
              placeholder={t("branding.pasteLinkHere")}
              onChange={handleChange}
              value={appStoreLink}
              disabled={!userIsEnableToUpdateAppStoreListing}
              sz="large"
            />
          </label>
          <label className="grow">
            <span>{t("branding.googlePlayLink")}</span>
            <input
              type="text"
              name="googlePlayLink"
              className="text-input"
              placeholder={t("branding.pasteLinkHere")}
              onChange={handleChange}
              value={googlePlayLink}
              disabled={!userIsEnableToUpdateAppStoreListing}
              sz="large"
            />
          </label>
        </div>
      </article>

      <article className="app-settings">
        <header>
          <p className="s1">{t("branding.appSettings")}</p>
        </header>
        <section>
          <div className="left-side">
            {viewportWidth && viewportWidth > 960 ? (
              <>
                <label>{t("branding.businessName")}</label>
                <input
                  type="text"
                  name="businessName"
                  className="text-input"
                  placeholder={t("branding.businessName")}
                  onChange={handleChange}
                  value={businessName}
                  sz="large"
                />
              </>
            ) : null}

            <label>{t("branding.mobileAppTheme")}</label>
            <ThemeSelector
              inputName="appTheme"
              label={t("branding.appTheme")}
              value={appTheme}
              onChange={handleSwitch}
              selectedBorder
            />
            <label>{t("branding.appColors")}</label>
            <MultiOptionsInput
              value={isAppDefaultColor ? "default" : "custom"}
              onChange={(name, value) => {
                handleChange({
                  target: {
                    name,
                    value: value === "custom" ? false : true,
                  },
                });
              }}
              options={["custom", "default"]}
              optionLabels={{
                custom: t("common:colors.custom"),
                default: t("common:colors.default"),
              }}
              inputName="isAppDefaultColor"
            />
            <label>{t("branding.appMainColor")}</label>
            <InputColor
              inputName="appMainColor"
              inputNameColor="appMainColorPicker"
              value={appMainColor}
              onChange={(evt) => {
                handleChange(evt);
              }}
            />
            <label>{t("branding.appSecondaryColorText")}</label>
            <MultiOptionsInput
              value={appSecondaryTextColor}
              onChange={(name, value) => {
                handleChange({
                  target: {
                    name,
                    value,
                  },
                });
              }}
              options={["white", "black"]}
              optionLabels={{
                white: t("common:colors.white"),
                black: t("common:colors.black"),
              }}
              inputName="appSecondaryTextColor"
            />
            <label>{t("branding.uploadInAppLogo")}</label>
            <ImageSelectorSmall
              id="Logo"
              inputName="customBranding[logoImg]"
              imageInputs={[
                { name: "logoChanged", value: logoChanged },
                {
                  name: "formLogoImgFile",
                  value: formLogoImg.file || "",
                },
                {
                  name: "formLogoImgType",
                  value: formLogoImg.type || "",
                },
                {
                  name: "formLogoImgName",
                  value: formLogoImg.name || "",
                },
                {
                  name: "formLogoImgS3Path",
                  value: formLogoImg.s3Path,
                },
              ]}
              onChange={handleChange}
              value={logoImg}
            />
          </div>
          <div className="right-side">
            {viewportWidth && viewportWidth <= 960 ? (
              <div role="input-group">
                <label>{t("branding.businessName")}</label>
                <input
                  type="text"
                  name="businessName"
                  className="text-input"
                  placeholder={t("branding.businessName")}
                  onChange={handleChange}
                  value={businessName}
                  sz="large"
                />
              </div>
            ) : null}
            {renderMobilePreview()}
          </div>
        </section>
      </article>
      <article>
        <header>
          <p className="s1">{t("branding.splashScreenSettings")}</p>
        </header>
        <section className="splash-screen">
          <div className="left-side">
            <div>
              <label>{t("branding.splashScreenLogo")}</label>
              <ImageSelector
                id="Splash Screen Logo"
                temporalValue={temporalSplashImg}
                selectorTitle={t("branding.uploadLogo")}
                subInfo={
                  <div>
                    <span>{t("branding.imageFormatsJpgSvgPng")}</span>
                    <span>{t("branding.maxSize800kb")}</span>
                  </div>
                }
                buttonTitle={t("common:edit")}
                // toolTipTextBody={
                //   "MSB will provide this asset for your storefront > how it works page"
                // }
                inputName="customBranding[mobileSplashImg]"
                imageInputs={[
                  {
                    name: "mobileSplashChanged",
                    value: mobileSplashChanged,
                  },
                  {
                    name: "formMobileSplashImgFile",
                    value: formMobileSplashImg.file || "",
                  },
                  {
                    name: "formMobileSplashImgType",
                    value: formMobileSplashImg.type || "",
                  },
                  {
                    name: "formMobileSplashImgName",
                    value: formMobileSplashImg.name || "",
                  },
                  {
                    name: "formMobileSplashImgS3Path",
                    value: formMobileSplashImg.s3Path,
                  },
                ]}
                value={mobileSplashImg}
                onChange={handleChange}
                additionalStyle={{
                  marginBottom: 16,
                  marginTop: 0,
                }}
                additionalContent={
                  <div className="switch-container">
                    <span>{t("branding.tintSplashscreenLogo")}</span>
                    <input
                      name="tintSplashImg"
                      value={isSplashImgTinted}
                      type="checkbox"
                      role="switch"
                      onChange={() => {
                        handleChange({
                          target: {
                            name: "tintSplashImg",
                            value: !isSplashImgTinted,
                          },
                        });
                      }}
                      checked={isSplashImgTinted}
                      sz="large"
                    />
                  </div>
                }
                showButton={false}
              />
            </div>
            <div>
              <label>{t("branding.splashScreenImage")}</label>
              <ImageSelector
                id="Splash Screen Image"
                temporalValue={temporalSplashBg}
                buttonTitle={t("common:edit")}
                additionalStyle={{
                  marginBottom: 16,
                  marginTop: 0,
                }}
                inputName="customBranding[mobileSplashBg]"
                showButton={false}
                imageInputs={[
                  {
                    name: "mobileSplashBgChanged",
                    value: mobileSplashBgChanged,
                  },
                  {
                    name: "formMobileSplashBgFile",
                    value: formMobileSplashBg.file || "",
                  },
                  {
                    name: "formMobileSplashBgType",
                    value: formMobileSplashBg.type || "",
                  },
                  {
                    name: "formMobileSplashBgName",
                    value: formMobileSplashBg.name || "",
                  },
                  {
                    name: "formMobileSplashBgS3Path",
                    value: formMobileSplashBg.s3Path,
                  },
                ]}
                value={mobileSplashBg}
                onChange={handleChange}
                selectorTitle={t("branding.uploadImage")}
                subInfo={
                  <div>
                    <span>{t("branding.imageFormatsJpgSvgPng")}</span>
                    <span>{t("branding.maxSize800kb")}</span>
                  </div>
                }
                additionalContent={
                  <div>
                    <div className="switch-container">
                      <span>{t("branding.overlay")}</span>
                      <input
                        name="splashOverlay"
                        value={splashOverlay}
                        type="checkbox"
                        role="switch"
                        onChange={() => {
                          handleChange({
                            target: {
                              name: "splashOverlay",
                              value: !splashOverlay,
                            },
                          });
                        }}
                        checked={splashOverlay}
                        sz="large"
                      />
                    </div>
                    {splashOverlay
                      ? [
                          <InputColor
                            inputName="splashOverlayColor"
                            inputNameColor="splashOverlayColorPicker"
                            value={splashOverlayColor}
                            onChange={handleChange}
                          />,

                          <div>
                            <div className="flex items-center justify-between mt-4">
                              <p className="p4">{t("branding.opacity")}</p>
                              <span>{splashOverlayOpacity}</span>
                            </div>

                            <GradientRange
                              onChange={onGradientRangeChange}
                              rangeName={"splashOverlayOpacity"}
                              rangeValue={splashOverlayOpacity}
                            />
                          </div>,
                        ]
                      : null}
                  </div>
                }
              />
            </div>
          </div>
          <div className="right-side">{renderMobileSplashPreview()}</div>
        </section>
      </article>
    </div>
  );
};
